'use client';
import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { useModel } from '@/context/ModelContext';

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value: number; name: string }>;
  label?: string;
}) {
  if (!active || !payload || !payload.length) return null;
  const val = payload[0].value ?? 0;
  return (
    <div
      style={{
        background: 'var(--surface-2)',
        border: '1px solid var(--divider)',
        borderRadius: '4px',
        padding: '10px 14px',
      }}
    >
      <p
        style={{
          fontSize: '0.7rem',
          fontWeight: 700,
          color: 'var(--text-primary)',
          marginBottom: 4,
        }}
      >
        {label}
      </p>
      <p
        style={{
          fontSize: '0.75rem',
          fontWeight: 700,
          fontFamily: 'var(--font-mono)',
          color: val >= 0 ? 'var(--upside)' : 'var(--downside)',
        }}
      >
        Cumulative FCF: ₹{val.toFixed(2)} Cr
      </p>
    </div>
  );
}

export default function CumulativeFcfChart() {
  const { model } = useModel();

  let cumulative = 0;
  const data = model.years.map((y) => {
    cumulative += y.projectFcf;
    return {
      name: y.year === 0 ? 'Yr 0' : `Yr ${y.year}`,
      cumulativeFcf: parseFloat((cumulative / 10000000).toFixed(2)),
    };
  });

  return (
    <ResponsiveContainer width="100%" height={200}>
      <AreaChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
        <defs>
          <linearGradient id="gradCumFcf" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="var(--gold)" stopOpacity={0.25} />
            <stop offset="95%" stopColor="var(--gold)" stopOpacity={0.01} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="2 4" stroke="var(--divider)" vertical={false} />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 10, fill: 'var(--text-tertiary)', fontWeight: 600 }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 10, fill: 'var(--text-tertiary)' }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v) => `₹${v}Cr`}
        />
        <Tooltip content={<CustomTooltip />} />
        <ReferenceLine
          y={0}
          stroke="var(--downside)"
          strokeDasharray="4 4"
          strokeWidth={1}
          label={{
            value: 'Breakeven',
            position: 'insideTopRight',
            fontSize: 9,
            fill: 'var(--downside)',
          }}
        />
        <Area
          type="monotone"
          dataKey="cumulativeFcf"
          stroke="var(--gold)"
          strokeWidth={2}
          fill="url(#gradCumFcf)"
          dot={{ fill: 'var(--gold)', r: 2.5, strokeWidth: 0 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
